<template>
  <v-dialog v-model="show" max-width="600" persistent>
    <v-card>
      <v-card-title style="font-weight: 500">
        <strong>
          {{ editingEvent ? 'Editar Agendamento' : 'Novo Agendamento' }}
        </strong>
      </v-card-title>

      <v-divider class="mt-4 mx-4"></v-divider>

      <v-card-text>
        <v-form ref="eventForm" v-model="valid">
          <label class="label">Padrão de Calibração</label>
          <v-autocomplete
            ref="patternSelect"
            v-model="eventForm.calibrationPattern"
            :items="calibrationV2Patterns"
            placeholder="Padrão de Calibração"
            item-text="name"
            class="primary-input"
            outlined
            multiple
            hide-details="auto"
            item-value="id"
            :rules="[rules.required]"
            :filter="customPatternFilter"
            @change="onPatternSelect"
            required
          >
            <template v-slot:item="{ item }">
              <v-list-item-content>
                <v-list-item-title>{{ item.name }}</v-list-item-title>
                <v-list-item-subtitle
                  >{{ item.model }} {{ '/' }} {{ item.propertyCode }} {{ '/' }}
                  {{ item.serialNumber }}</v-list-item-subtitle
                >
              </v-list-item-content>
            </template>
          </v-autocomplete>

          <v-row class="ma-0 mb-4">
            <v-col cols="6">
              <label class="label">Data de Início</label>
              <div class="d-flex mb-3 mt-1">
                <v-icon class="mr-1">mdi-calendar</v-icon>
                <date-picker
                  ref="startDateTimePicker"
                  style="width: 100%"
                  v-model="eventForm.startDateTime"
                  mode="date"
                  locale="pt-BR"
                  :first-day-of-week="1"
                  is24hr
                  :popover="{ visibility: 'click', positionFixed: 'true' }"
                >
                  <template v-slot="{ inputValue, inputEvents }">
                    <input
                      class="date-input"
                      :value="inputValue"
                      v-on="inputEvents"
                      placeholder="Data de Início"
                      v-mask="['##/##/####']"
                    />
                  </template>
                </date-picker>
              </div>
            </v-col>

            <v-col cols="6">
              <label class="label">Data de Término</label>
              <div class="d-flex mb-3 mt-1">
                <v-icon class="mr-1">mdi-calendar</v-icon>
                <date-picker
                  ref="endDateTimePicker"
                  style="width: 100%"
                  v-model="eventForm.endDateTime"
                  mode="date"
                  locale="pt-BR"
                  :first-day-of-week="1"
                  is24hr
                  :popover="{ visibility: 'click', positionFixed: 'true' }"
                >
                  <template v-slot="{ inputValue, inputEvents }">
                    <input
                      class="date-input"
                      :value="inputValue"
                      v-on="inputEvents"
                      placeholder="Data de Término"
                      v-mask="['##/##/####']"
                    />
                  </template>
                </date-picker>
              </div>
            </v-col>
          </v-row>

          <label class="label">Evento</label>
          <v-select
            v-model="eventForm.schedulingEventType"
            :items="schedulingEventTypes"
            item-text="label"
            item-value="value"
            placeholder="Selecione o tipo de evento"
            class="primary-input"
            outlined
            :rules="[rules.required]"
            required
          >
            <template v-slot:item="{ item }">
              <v-list-item-content>
                <v-list-item-title>{{ item.label }}</v-list-item-title>
              </v-list-item-content>
            </template>
          </v-select>

          <template v-if="eventType === 'Translado'">
            <label class="label">Tipo de Translado</label>
            <v-autocomplete
              v-model="eventForm.transladoType"
              :items="transladoOptions"
              placeholder="Selecione o tipo de translado"
              item-text="text"
              item-value="value"
              hide-details="auto"
              class="primary-input"
              outlined
              :rules="[rules.required]"
              required
            >
              <template v-slot:item="{ item }">
                <v-list-item-content>
                  <v-list-item-title>{{ item.text }}</v-list-item-title>
                </v-list-item-content>
              </template>
            </v-autocomplete>
          </template>

          <template
            v-if="eventType === 'Empréstimo' || (eventType === 'Translado' && eventForm.transladoType === 'institution')"
          >
            <label class="label">Instituição de Destino</label>
            <v-autocomplete
              v-model="eventForm.destinationInstitution"
              :items="institutions"
              placeholder="Instituição de Destino"
              item-text="name"
              item-value="id"
              hide-details="auto"
              class="primary-input"
              outlined
              :rules="[rules.required]"
              required
            >
              <template v-slot:item="{ item }">
                <v-list-item-content>
                  <v-list-item-title>{{ item.name }}</v-list-item-title>
                  <v-list-item-subtitle>{{
                    item.address
                  }}</v-list-item-subtitle>
                </v-list-item-content>
              </template>
            </v-autocomplete>

            <label class="label">Unidade de Destino</label>
            <v-autocomplete
              v-model="eventForm.destinationUnidade"
              :items="unidades"
              placeholder="Unidade de Destino"
              item-text="name"
              item-value="id"
              hide-details="auto"
              class="primary-input"
              outlined
              :rules="[rules.required]"
              required
            >
              <template v-slot:item="{ item }">
                <v-list-item-content>
                  <v-list-item-title>{{ item.name }}</v-list-item-title>
                  <v-list-item-subtitle>{{
                    item.address
                  }}</v-list-item-subtitle>
                </v-list-item-content>
              </template>
            </v-autocomplete>
          </template>

          <template v-if="eventType === 'Calibração' || (eventType === 'Translado' && eventForm.transladoType === 'supplier')">
            <label class="label">Fornecedor</label>
            <v-autocomplete
              v-model="eventForm.supplierId"
              :items="suppliers"
              placeholder="Fornecedor"
              item-text="name"
              item-value="id"
              hide-details="auto"
              class="primary-input"
              outlined
              :rules="[rules.required]"
              required
            ></v-autocomplete>
          </template>

          <label class="label">Observação (opcional)</label>
          <v-textarea
            v-model="eventForm.details"
            placeholder="Detalhes (opcional)"
            rows="3"
            hide-details="auto"
            class="primary-input"
            outlined
          ></v-textarea>

          <v-checkbox
            class="my-2 mr-2"
            hide-details
            label="Receber alerta"
            v-model="eventForm.alert"
          ></v-checkbox>

          <div v-if="eventForm.alert">
            <v-col cols="12">
              <label class="label">Data e Hora do alerta</label>
              <div class="d-flex mb-3 mt-1">
                <v-icon class="mr-1">mdi-calendar</v-icon>
                <date-picker
                  ref="alertDateTimePicker"
                  style="width: 100%"
                  v-model="eventForm.alertDateTime"
                  mode="dateTime"
                  is24hr
                  :popover="{ visibility: 'click', positionFixed: 'true' }"
                >
                  <template v-slot="{ inputValue, inputEvents }">
                    <input
                      class="date-input"
                      :value="inputValue"
                      v-on="inputEvents"
                      placeholder="Data e Hora do alerta"
                      v-mask="['##/##/#### ##:##']"
                    />
                  </template>
                </date-picker>
              </div>
            </v-col>
          </div>
        </v-form>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text @click="close" depressed>Cancelar</v-btn>
        <v-btn color="primary" @click="saveEvent" :disabled="!valid" depressed>
          {{ editingEvent ? 'Atualizar' : 'Criar' }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import ApiService from '@/services/ApiService.js'

export default {
  name: 'EventFormDialog',
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    editingEvent: {
      type: Boolean,
      default: false,
    },
    eventData: {
      type: Object,
      default: () => ({}),
    },
    schedulingEventTypes: {
      type: Array,
      default: () => [
        { name: 'Loan', value: 0, label: 'Empréstimo' },
        { name: 'Translado', value: 1, label: 'Translado' },
        { name: 'Calibration', value: 2, label: 'Calibração' },
      ],
    },
    institution: {
      type: Object,
      default: null,
    },
    suppliers: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      apiService: new ApiService(),
      valid: false,
      institutions: [],
      unidades: [],
      calibrationV2Patterns: [],
      manufacturers: [],
      loading: false,
      transladoOptions: [
        { text: 'Para Instituição', value: 'institution' },
        { text: 'Para Fornecedor', value: 'supplier' },
      ],
      eventForm: {
        name: '',
        startDateTime: '',
        endDateTime: '',
        calibrationPattern: [],
        destinationInstitution: null,
        destinationUnidade: null,
        alert: false,
        alertDateTime: '',
        schedulingEventType: 0,
        details: '',
        supplierId: null,
        transladoType: null,
      },
      rules: {
        required: (value) => value !== null && value !== undefined && value !== '' ? true : 'Obrigatório.',
      },
    }
  },
  computed: {
    show: {
      get() {
        return this.value
      },
      set(value) {
        this.$emit('input', value)
      },
    },
    eventType() {
      const selected = this.schedulingEventTypes.find(
        (x) => x.value === this.eventForm.schedulingEventType
      )
      return selected?.label || ''
    },
    isTransladoEvent() {
      return this.eventType === 'Translado'
    },
  },
  watch: {
    eventData: {
      handler(newData) {
        if (newData && Object.keys(newData).length > 0) {
          this.eventForm = { ...newData }
          if (
            newData.schedulingPatterns &&
            Array.isArray(newData.schedulingPatterns)
          ) {
            this.eventForm.calibrationPattern =
              newData.schedulingPatterns.length > 0
                ? newData.schedulingPatterns.map((p) => p.patternId)
                : []
          }
          // Default to 0 (Loan) if not set
          if (typeof this.eventForm.schedulingEventType !== 'number') {
            this.eventForm.schedulingEventType = 0
          }
          if (this.editingEvent && this.eventType === 'Translado') {
            if (newData.supplierId) {
              this.eventForm.transladoType = 'supplier'
            } else if (newData.toInstitutionId || newData.destinationInstitution) {
              this.eventForm.transladoType = 'institution'
            }
          }
        }
      },
      immediate: true,
      deep: true,
    },
    value(newVal) {
      if (newVal) {
        this.onModalOpen()
      } else {
        this.resetForm()
      }
    },
    'eventForm.destinationInstitution': {
      immediate: true,
      handler(newInstitutionId) {
        if (newInstitutionId) {
          this.getUnidades(newInstitutionId)
          if (!this.editingEvent) {
            this.eventForm.destinationUnidade = null
          }
        }
      },
    },
    'eventForm.transladoType': {
      handler(newType) {
        // Limpa os campos quando muda o tipo de translado (apenas se não estiver editando)
        if (!this.editingEvent) {
          if (newType === 'institution') {
            this.eventForm.supplierId = null
          } else if (newType === 'supplier') {
            this.eventForm.destinationInstitution = null
            this.eventForm.destinationUnidade = null
          }
        }
      },
    },
    eventType: {
      handler(newEventType) {
        // Detectar tipo de translado quando o eventType mudar para Translado durante edição
        if (this.editingEvent && newEventType === 'Translado' && !this.eventForm.transladoType) {
          this.$nextTick(() => {
            if (this.eventData.supplierId) {
              this.eventForm.transladoType = 'supplier'
            } else if (this.eventData.toInstitutionId || this.eventData.destinationInstitution) {
              this.eventForm.transladoType = 'institution'
            }
          })
        }
      },
    },
  },
  mounted() {
    window.addEventListener('keydown', this.handleKeyDown)
  },
  beforeDestroy() {
    window.removeEventListener('keydown', this.handleKeyDown)
  },
  methods: {
    handleKeyDown(e) {
      if (e.key === 'Escape' && this.show) {
        this.close()
      }
    },

    async onModalOpen() {
      await this.getPattern()
      this.getInstitutions()

      if (this.editingEvent && this.eventData?.schedulingPatterns?.length) {
        this.eventForm.calibrationPattern =
          this.eventData.schedulingPatterns.map((p) => p.patternId)
      }

      // Detectar tipo de translado na edição
      if (this.editingEvent && this.eventType === 'Translado') {
        if (this.eventData.supplierId) {
          this.eventForm.transladoType = 'supplier'
        } else if (this.eventData.toInstitutionId || this.eventData.destinationInstitution) {
          this.eventForm.transladoType = 'institution'
        }
      }
    },

    async getPattern() {
      await this.apiService
        .get(`calibration/v2/pattern/list/${this.institution.id}`)
        .then((response) => {
          this.calibrationV2Patterns = response.content
        })
    },
    getInstitutions() {
      this.apiService.get('institution/list').then((response) => {
        this.institutions = response.content
      })
    },
    async getUnidades(institutionId) {
      await this.apiService
        .get(`unidade/list/${institutionId}`)
        .then((response) => {
          this.unidades = response.content
        })
    },

    onPatternSelect() {
      // Força o fechamento do dropdown após seleção
      this.$nextTick(() => {
        if (this.$refs.patternSelect) {
          this.$refs.patternSelect.blur()
          this.$refs.patternSelect.isMenuActive = false
        }
      })
    },

    customPatternFilter(item, queryText) {
      // Busca customizada que procura em nome, número de série e patrimônio
      const searchText = queryText.toLowerCase()
      const name = (item.name || '').toLowerCase()
      const serialNumber = (item.serialNumber || '').toLowerCase()
      const propertyCode = (item.propertyCode || '').toLowerCase()
      const model = (item.model || '').toLowerCase()
      
      return name.includes(searchText) || 
             serialNumber.includes(searchText) || 
             propertyCode.includes(searchText) ||
             model.includes(searchText)
    },

    close() {
      this.show = false
    },
    async saveEvent() {
      if (
        this.$refs.eventForm.validate() &&
        this.eventForm.startDateTime &&
        this.eventForm.endDateTime
      ) {
        // Garante que schedulingEventType é sempre um número válido do item selecionado
        // Corrige o tipo para garantir que é number
        this.eventForm.schedulingEventType = Number(this.eventForm.schedulingEventType)
        const selectedType = this.schedulingEventTypes.find(
          t => t.value === this.eventForm.schedulingEventType || t.label === this.eventForm.schedulingEventType || t.name === this.eventForm.schedulingEventType
        )
        const schedulingEventTypeValue = selectedType ? selectedType.value : 0
        this.eventForm.schedulingEventType = schedulingEventTypeValue
        const timezoneHeader = {
          headers: {
            institutiontimezone:
              Intl.DateTimeFormat().resolvedOptions().timeZone,
          },
        }

        try {
          if (this.editingEvent && this.eventForm.id) {
            // EDIÇÃO - usar endpoint PUT /edit
            const schedulingEdit = {
              id: this.eventForm.id,
              fromInstitutionId: this.institution.id,
              toInstitutionId: this.eventForm.destinationInstitution,
              toUnidadeId: this.eventForm.destinationUnidade,
              startDate: new Date(this.eventForm.startDateTime),
              endDate: new Date(this.eventForm.endDateTime),
              alertDate: this.eventForm.alertDateTime ? new Date(this.eventForm.alertDateTime) : null,
              color: this.eventForm.color,
              details: this.eventForm.details,
              supplierId: this.eventForm.supplierId,
              schedulingEventType: this.eventForm.schedulingEventType,
              schedulingPatterns: this.eventForm.calibrationPattern.map(
                (patternId) => ({
                  patternId: patternId,
                  patternRequestId: null,
                })
              ),
            }
            
            await this.apiService.put(
              'scheduling/edit',
              schedulingEdit,
              timezoneHeader
            )
            this.$toast.success('Agendamento atualizado com sucesso!')
          } else {
            // CRIAÇÃO - usar endpoint POST /add
            const schedulingAdd = {
              institutionId: this.institution.id,
              toUnidadeId: this.eventForm.destinationUnidade,
              fromInstitutionId: this.institution.id,
              toInstitutionId: this.eventForm.destinationInstitution,
              userId: this.eventForm.userId,
              startDate: new Date(this.eventForm.startDateTime),
              endDate: new Date(this.eventForm.endDateTime),
              alertDate: this.eventForm.alertDateTime ? new Date(this.eventForm.alertDateTime) : null,
              color: this.eventForm.color,
              details: this.eventForm.details,
              supplierId: this.eventForm.supplierId,
              schedulingEventType: this.eventForm.schedulingEventType,
              schedulingPatterns: this.eventForm.calibrationPattern.map(
                (patternId) => ({
                  patternId: patternId,
                  patternRequestId: null,
                })
              ),
            }
            
            await this.apiService.post(
              'scheduling/add',
              schedulingAdd,
              timezoneHeader
            )
            this.$toast.success('Agendamento criado com sucesso!')
          }

          // Emitir evento para atualizar o calendário
          this.$emit('event-saved')
          this.close()
        } catch (error) {
          console.error('Erro ao salvar agendamento:', error)
          // O toast de erro já é exibido automaticamente pelo handler global em main.js
        }
      }
    },

    resetForm() {
      this.eventForm = {
        name: '',
        startDateTime: '',
        endDateTime: '',
        calibrationPattern: [],
        destinationInstitution: null,
        color: '#2196f3',
        details: '',
        transladoType: null,
      }
      this.$refs.eventForm?.resetValidation()
    },
  },
}
</script>

<style scoped>
.date-input {
  background: var(--primary-input-background) !important;
  font-family: var(--primary-input-font) !important;
  font-size: var(--primary-input-font-size) !important;
  font-weight: var(--primary-input-font-weight) !important;
  line-height: var(--primary-input-line-height) !important;
  letter-spacing: var(--primary-input-letter-spacing) !important;
  border-radius: var(--primary-input-border-radius) !important;
  height: 45px;
  width: 100%;
  border: 1px solid #707070 !important;
  padding-left: 10px !important;
}
</style>
